import React, { createContext, useState, useEffect, useRef } from 'react';

const AuthContext = createContext();

const TOKEN_SKEW_MS = 60000; // 1 minute skew to avoid edge expiry

function isJwtExpired(token) {
  if (!token) return true;
  try {
    const claims = parseJwt(token);
    if (!claims?.exp) return true;
    const now = Date.now();
    return claims.exp * 1000 <= (now + TOKEN_SKEW_MS);
  } catch {
    return true;
  }
}

function loadStoredTokens() {
  try {
    const raw = localStorage.getItem('cognitoTokens');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Keep whatever is stored (even if id_token is expired) so we can attempt refresh on boot.
    // Only clear on parse errors.
    return parsed;
  } catch {
    localStorage.removeItem('cognitoTokens');
    return null;
  }
}

export function AuthProvider({ children }) {
  // authData holds the full /whoami response: { claims, user, manager_roles }
  // Initialize tokens from localStorage to avoid redirect loops on reload
  const [tokens, setTokens] = useState(loadStoredTokens);
  // Initialize authData with JWT claims if token exists
  const [authData, setAuthData] = useState(() => {
    if (tokens?.id_token) {
      const claims = parseJwt(tokens.id_token);
      return { claims };
    }
    return null;
  });
  // Start in loading state if we already have stored tokens so the UI doesn't flash logged-out
  const [isLoading, setIsLoading] = useState(() => Boolean(tokens?.id_token));
  // Base URL for API to fetch user info
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  // Heartbeat interval: configurable via env
  const SESSION_HEARTBEAT_INTERVAL_MS = parseInt(
    process.env.REACT_APP_SESSION_HEARTBEAT_INTERVAL_MS || '60000',
    10
  );

  useEffect(() => {
    // If redirected back with tokens in hash or authorization code, parse and store them
    const { hash, search, pathname } = window.location;

    // Handle explicit logged_out route
    if (pathname === '/logged_out') {
      localStorage.removeItem('cognitoTokens');
      setTokens(null);
      setAuthData(null);
      return;
    }

    // Prefer Authorization Code + PKCE exchange if a code is present
    const query = new URLSearchParams(search);
    const err = query.get('error');
    const errDesc = query.get('error_description');
    if (err) {
      console.error('OAuth error from Hosted UI:', err, errDesc);
      // Clean up URL to avoid repeated handling
      window.history.replaceState(null, document.title, window.location.pathname);
      return;
    }
    const code = query.get('code');
    if (code) {
      exchangeCodeForTokens(code)
        .then((tok) => {
          if (tok) {
            localStorage.setItem('cognitoTokens', JSON.stringify(tok));
            setTokens(tok);
            const claims = parseJwt(tok.id_token);
            setAuthData({ claims });
          }
        })
        .finally(() => {
          // Clean up URL (remove code, state)
          window.history.replaceState(null, document.title, window.location.pathname);
        });
      return;
    }

    // Fallback: support legacy implicit flow if tokens in hash
    if (hash) {
      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const idToken = hashParams.get('id_token');
      const accessToken = hashParams.get('access_token');
      if (idToken) {
        const newTokens = { id_token: idToken, access_token: accessToken };
        localStorage.setItem('cognitoTokens', JSON.stringify(newTokens));
        setTokens(newTokens);
        const claims = parseJwt(idToken);
        setAuthData({ claims });
        window.history.replaceState(null, document.title, window.location.pathname);
      }
    }
  }, []);
  // Fetch user details from API when a valid id_token is available
  // Single-flight ref to avoid overlapping whoami/refresh calls
  const whoamiInFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const restoreSession = async () => {
      if (whoamiInFlightRef.current) return;
      const hasIdToken = !!tokens?.id_token;
      if (!hasIdToken) return;
      const idExpired = isJwtExpired(tokens.id_token);
      // If we already have a user and the current token is still valid, do nothing
      if (authData?.user && !idExpired) return;
      whoamiInFlightRef.current = true;
      setIsLoading(true);
      let activeTokens = tokens;
      try {
        if (idExpired) {
          if (!tokens.refresh_token) {
            throw new Error('ID token expired and no refresh token available');
          }
          const refreshed = await refreshTokens(tokens.refresh_token);
          if (!refreshed?.id_token) {
            throw new Error('Refresh token call did not return a new id_token');
          }
          activeTokens = refreshed;
          if (!cancelled) {
            setTokens(refreshed);
            localStorage.setItem('cognitoTokens', JSON.stringify(refreshed));
            const refreshedClaims = parseJwt(refreshed.id_token);
            setAuthData((prev) => {
              if (!prev) return { claims: refreshedClaims };
              return { ...prev, claims: refreshedClaims };
            });
          }
        }
        const res = await fetch(`${apiBase}/whoami`, {
          headers: { Authorization: `Bearer ${activeTokens.id_token}` },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch user info: ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setAuthData(data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error restoring session:', err);
        localStorage.removeItem('cognitoTokens');
        setTokens(null);
        setAuthData(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
        whoamiInFlightRef.current = false;
      }
    };
    restoreSession();
    return () => {
      cancelled = true;
      controller.abort();
      whoamiInFlightRef.current = false;
    };
  }, [tokens?.id_token, tokens?.refresh_token, apiBase, authData?.user]);

  // Authenticated heartbeat polling to keep session awareness
  useEffect(() => {
    const hasUser = !!authData?.user;
    const idToken = tokens?.id_token;
    if (!idToken || !hasUser) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const beat = async () => {
      try {
        const res = await fetch(`${apiBase}/heartbeat`, {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: controller.signal,
        });
        // 401 is handled globally; no-op here
        return res.ok;
      } catch (e) {
        // Ignore network errors; next tick will try again
        return false;
      }
    };
    // Initial beat (non-blocking)
    beat();
    const id = setInterval(() => {
      if (!cancelled) beat();
    }, SESSION_HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      controller.abort();
    };
  }, [authData?.user, tokens?.id_token, apiBase, SESSION_HEARTBEAT_INTERVAL_MS]);

  // Auto-refresh id_token shortly before it expires using refresh_token
  const refreshTimerRef = useRef(null);
  useEffect(() => {
    // Clear any existing timer when tokens change
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const idTok = tokens?.id_token;
    const refreshTok = tokens?.refresh_token;
    if (!idTok) return;
    const claims = parseJwt(idTok);
    if (!claims?.exp) return;
    const expMs = claims.exp * 1000;
    // Refresh 2 minutes before expiry (configurable via env)
    const leadMs = parseInt(process.env.REACT_APP_TOKEN_REFRESH_LEEWAY_MS || '120000', 10);
    const now = Date.now();
    const delay = Math.max(0, expMs - now - leadMs);
    // If no refresh token is available, skip scheduling; user will need to re-login on expiry
    if (!refreshTok) {
      if (delay > 0) {
        refreshTimerRef.current = setTimeout(() => {
          console.warn('No refresh_token available; clearing tokens before expiry');
          localStorage.removeItem('cognitoTokens');
          setTokens(null);
          setAuthData(null);
        }, delay);
      }
      return;
    }
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const updated = await refreshTokens(refreshTok);
        if (updated?.id_token) {
          // Silent: persist and update state so consumers pick up the new token
          localStorage.setItem('cognitoTokens', JSON.stringify(updated));
          setTokens(updated);
          const refreshedClaims = parseJwt(updated.id_token);
          setAuthData((prev) => {
            if (!prev) return { claims: refreshedClaims };
            return { ...prev, claims: refreshedClaims };
          });
        } else {
          // If refresh failed to return new tokens, clear session
          localStorage.removeItem('cognitoTokens');
          setTokens(null);
          setAuthData(null);
        }
      } catch (e) {
        console.error('Token refresh failed', e);
        localStorage.removeItem('cognitoTokens');
        setTokens(null);
        setAuthData(null);
      }
    }, delay);
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [tokens]);

  // Temporary: force periodic refresh using refresh_token every N ms (default 20s)
  const FORCE_REFRESH_INTERVAL_MS = parseInt(
    // Default to 0 (disabled) unless explicitly enabled via env
    process.env.REACT_APP_FORCE_REFRESH_INTERVAL_MS || '0',
    10
  );
  useEffect(() => {
    const refreshTok = tokens?.refresh_token;
    if (!refreshTok || !FORCE_REFRESH_INTERVAL_MS) return;
    let cancelled = false;
    const id = setInterval(async () => {
      if (cancelled) return;
      try {
        const updated = await refreshTokens(refreshTok);
        if (updated?.id_token) {
          // Silent: persist and update state so consumers pick up the new token
          localStorage.setItem('cognitoTokens', JSON.stringify(updated));
          setTokens(updated);
          const refreshedClaims = parseJwt(updated.id_token);
          setAuthData((prev) => {
            if (!prev) return { claims: refreshedClaims };
            return { ...prev, claims: refreshedClaims };
          });
        }
      } catch (e) {
        // swallow errors in forced refresh loop
      }
    }, FORCE_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [tokens?.refresh_token, FORCE_REFRESH_INTERVAL_MS]);

  const login = async () => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    // Always redirect back to the current origin after sign-in
    const redirectUri = window.location.origin;
    // log for debugging wrong env or URL
    console.log('⚙️ Auth Params →', { domain, clientId, redirectUri });
    // validate domain: must be your Hosted UI domain, not the IDP API endpoint
    if (!domain || domain.includes('cognito-idp')) {
      console.error(
        `Invalid Cognito domain: '${domain}'. REACT_APP_COGNITO_DOMAIN must be your User Pool Hosted UI domain, e.g. 'your-pool-domain.auth.us-east-1.amazoncognito.com'`
      );
    }
    // Use Authorization Code + PKCE to obtain refresh_token
    // Request only allowed OIDC scopes; configurable via env
    const scope = (process.env.REACT_APP_OAUTH_SCOPES || 'openid email profile')
      .split(/[,\s]+/)
      .filter(Boolean)
      .join(' ');
    const { verifier, challenge } = await createPkcePair();
    // Persist verifier for post-redirect token exchange
    sessionStorage.setItem('pkce_verifier', verifier);
    const url = `https://${domain}/oauth2/authorize?response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&code_challenge_method=S256&code_challenge=${encodeURIComponent(challenge)}`;

    
    //const url = "https://veravas-portal-dev.auth.us-east-1.amazoncognito.com/login?response_type=code&client_id=your-client-id&redirect_uri=http://localhost:3000/";
    console.log('🔗 Redirecting to:', url);
    window.location.assign(url);
  };

  const logout = () => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    // Always redirect back to the current origin's logged_out path after sign-out
    const redirectUri = `${window.location.origin}/logged_out`;
    // clear storage
    localStorage.removeItem('cognitoTokens');
    setTokens(null);
    // clear auth data
    setAuthData(null);
    const url = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      redirectUri
    )}`;
    console.log('🔗 Redirecting to:', url);
    window.location.assign(url);
  };

  return (
    <AuthContext.Provider
      value={{
        // expose only the inner user object (or null), plus claims and roles
        user: authData?.user ?? null,
        claims: authData?.claims ?? null,
        managerRoles: authData?.manager_roles ?? [],
        tokens,
        authData,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT', e);
    return null;
  }
}

// --- OAuth2 / PKCE helpers ---
async function createPkcePair() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  const verifier = base64UrlEncode(array);
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(new Uint8Array(digest));
  return { verifier, challenge };
}

function base64UrlEncode(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function exchangeCodeForTokens(code) {
  try {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
    const redirectUri = window.location.origin;
    const verifier = sessionStorage.getItem('pkce_verifier');
    if (!verifier) {
      console.error('Missing PKCE verifier; cannot exchange code');
      return null;
    }
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    });
    const resp = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!resp.ok) {
      console.error('Token exchange failed', resp.status, await resp.text());
      return null;
    }
    const data = await resp.json();
    const out = {
      id_token: data.id_token,
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      token_type: data.token_type,
      expires_in: data.expires_in,
    };
    // Clear one-time verifier
    sessionStorage.removeItem('pkce_verifier');
    return out;
  } catch (e) {
    console.error('Error exchanging code for tokens', e);
    return null;
  }
}

async function refreshTokens(refreshToken) {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });
  const resp = await fetch(`https://${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) {
    console.error('Refresh token request failed', resp.status, await resp.text());
    return null;
  }
  const data = await resp.json();
  // Cognito may not return a new refresh_token; retain existing
  const stored = (() => {
    try { return JSON.parse(localStorage.getItem('cognitoTokens') || 'null'); } catch { return null; }
  })();
  return {
    id_token: data.id_token || stored?.id_token,
    access_token: data.access_token || stored?.access_token,
    refresh_token: data.refresh_token || stored?.refresh_token,
    token_type: data.token_type || stored?.token_type,
    expires_in: data.expires_in || stored?.expires_in,
  };
}

export default AuthContext;
